import { memo, useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faPhoneSlash,
  faMicrophone,
  faMicrophoneSlash,
  faVolumeHigh,
  faVolumeMute,
  faTimes,
  faGripLines,
} from '@fortawesome/free-solid-svg-icons';
import { getToken } from '../../../function/token';
import { acceptSDPoffer, endCallSession, toggleMicrophoneMute, toggleSpeakerMute } from '../../../function/Calling';
import { ErrorToastContainer } from '../../../function/CustomFunction';
import domain from '../../../../reusecomponent/domain';

// ─── Ringtone Synthesizer (Web Audio API) ────────────────────────────────────
let ringtoneContext = null;
let ringtoneTimer = null;

function startRingtone() {
  stopRingtone();
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    ringtoneContext = new AudioCtx();

    const playBeepPair = () => {
      if (!ringtoneContext || ringtoneContext.state === 'closed') return;
      const now = ringtoneContext.currentTime;

      // Primary tone (440Hz)
      const osc1 = ringtoneContext.createOscillator();
      const g1 = ringtoneContext.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(440, now);
      g1.gain.setValueAtTime(0.1, now);
      g1.gain.exponentialRampToValueAtTime(0.001, now + 1.2);
      osc1.connect(g1);
      g1.connect(ringtoneContext.destination);
      osc1.start(now);
      osc1.stop(now + 1.2);

      // Secondary tone (480Hz)
      const osc2 = ringtoneContext.createOscillator();
      const g2 = ringtoneContext.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(480, now);
      g2.gain.setValueAtTime(0.1, now);
      g2.gain.exponentialRampToValueAtTime(0.001, now + 1.2);
      osc2.connect(g2);
      g2.connect(ringtoneContext.destination);
      osc2.start(now);
      osc2.stop(now + 1.2);
    };

    playBeepPair();
    ringtoneTimer = setInterval(playBeepPair, 3000);
  } catch (e) {
    console.error('Ringtone play error:', e);
  }
}

function stopRingtone() {
  if (ringtoneTimer) {
    clearInterval(ringtoneTimer);
    ringtoneTimer = null;
  }
  if (ringtoneContext) {
    try {
      ringtoneContext.close();
    } catch (e) {}
    ringtoneContext = null;
  }
}

// ─── CallTimer Component ──────────────────────────────────────────────────────
function CallTimer({ active }) {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!active) {
      setSeconds(0);
      return;
    }
    const interval = setInterval(() => setSeconds(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, [active]);

  const formatTime = (secs) => {
    const mins = Math.floor(secs / 60);
    const s = secs % 60;
    return `${mins.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className="badge bg-success-subtle text-success border border-success-subtle px-3 py-1.5 rounded-pill mt-2 d-inline-flex align-items-center gap-1"
      style={{ fontSize: '14px', fontWeight: '600', letterSpacing: '0.5px' }}
    >
      <span className="me-1 text-danger animate-pulse">●</span> {formatTime(seconds)}
    </div>
  );
}

// ─── Call Component ──────────────────────────────────────────────────────────
function Call({ caller_detils, caller_exits, visible, setVisible, calling_status, setCallingStatus }) {
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerMuted, setIsSpeakerMuted] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const incomingCallService = useSelector((state) => state?.serviceStore?.value?.incoming_call);
  const isIncomingCallEnabled = Boolean(incomingCallService && incomingCallService !== "0" && incomingCallService !== 0);

  const callRecordingService = useSelector((state) => 
    state?.serviceStore?.value?.call_recording ?? 
    state?.serviceStore?.value?.call_recoding ?? 
    state?.serviceStore?.value?.recording
  );
  const isCallRecordingEnabled = Boolean(
    callRecordingService && 
    callRecordingService !== "0" && 
    callRecordingService !== 0
  );

  // Dragging state and position
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, initialX: 0, initialY: 0 });
  const sdpAnswerRef = useRef(null);

  const isUserInitiated = caller_detils?.current?.direction === 'USER_INITIATED';
  const isBusinessInitiated = caller_detils?.current?.direction === 'BUSINESS_INITIATED';

  // Position reset and timer sync when modal visibility changes
  useEffect(() => {
    if (visible) {
      const initialX = Math.max(20, Math.floor((window.innerWidth - 340) / 2));
      const initialY = Math.max(20, Math.floor((window.innerHeight - 460) / 2));
      setPosition({ x: initialX, y: initialY });
      if (calling_status !== 'Connected' && calling_status?.toLowerCase?.() !== 'connected') {
        setIsTimerActive(false);
      }
    } else {
      setIsTimerActive(false);
      setIsConnecting(false);
    }
  }, [visible, calling_status]);

  // Handle Drag Start
  const handleMouseDown = (e) => {
    if (e.target.closest('button')) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = {
      startX: clientX,
      startY: clientY,
      initialX: position.x,
      initialY: position.y,
    };
    setIsDragging(true);
  };

  // Drag Move & End Listener
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const deltaX = clientX - dragRef.current.startX;
      const deltaY = clientY - dragRef.current.startY;

      const newX = Math.min(
        Math.max(10, dragRef.current.initialX + deltaX),
        window.innerWidth - 350
      );
      const newY = Math.min(
        Math.max(10, dragRef.current.initialY + deltaY),
        window.innerHeight - 100
      );

      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleMouseMove);
    window.addEventListener('touchend', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);

  // Handle ringtone playback for incoming calls
  useEffect(() => {
    if (visible && isUserInitiated && !isTimerActive && isIncomingCallEnabled) {
      startRingtone();
    } else {
      stopRingtone();
    }
    return () => stopRingtone();
  }, [visible, isUserInitiated, isTimerActive, isIncomingCallEnabled]);

  // Derive display values from refs
  let name = 'No Name';
  let phone = '+910000000000';
  let ProfilePic = null;

  if (caller_exits?.current) {
    name = caller_exits.current.name || 'No Name';
    phone = caller_exits.current.wa_id || '+910000000001';
    ProfilePic = caller_exits.current.profile_pic || caller_exits.current?.name?.charAt(0) || 'N';
  } else {
    name = caller_detils?.current?.name || 'No Name';
    phone = caller_detils?.current?.wa_id || '+910000000001';
    ProfilePic = caller_detils?.current?.profile_pic || caller_detils?.current?.name?.charAt(0) || 'N';
  }

  const closeModal = useCallback(() => {
    try { sessionStorage.removeItem('active_accepted_call_id'); } catch (_) {}
    stopRingtone();
    endCallSession();
    setIsTimerActive(false);
    setIsMuted(false);
    setIsSpeakerMuted(false);
    setVisible(false);
  }, [setVisible]);

  useEffect(() => {
    if (calling_status === 'Connected' || calling_status?.toLowerCase?.() === 'connected') {
      setIsTimerActive(true);
      setIsConnecting(false);
      stopRingtone();
    } else {
      setIsTimerActive(false);
    }
  }, [calling_status]);

  const handleToggleMute = useCallback(() => {
    const nextMute = !isMuted;
    setIsMuted(nextMute);
    toggleMicrophoneMute(nextMute);
  }, [isMuted]);

  const handleToggleSpeaker = useCallback(() => {
    const nextSpeakerMute = !isSpeakerMuted;
    setIsSpeakerMuted(nextSpeakerMute);
    toggleSpeakerMute(nextSpeakerMute);
  }, [isSpeakerMuted]);

  async function callAccept() {
    stopRingtone();
    setIsConnecting(true);

    if (!caller_detils?.current?.session?.sdp) {
      ErrorToastContainer('Invalid call offer!');
      setIsConnecting(false);
      return;
    }

    try {
      const browsersdp = await acceptSDPoffer(caller_detils.current.session.sdp);
      if (!browsersdp) {
        setIsConnecting(false);
        return;
      }

      sdpAnswerRef.current = browsersdp;

      const token = getToken();
      const myHeaders = new Headers({ Authorization: `Bearer ${token}` });
      const formdata = new FormData();
      formdata.append('call_id', caller_detils.current.id);
      formdata.append('action', 'accept');
      formdata.append('source', 'web');
      formdata.append('sdp_answer', browsersdp);

      const endpoint = `${domain()}/call_accept`;

      if (isCallRecordingEnabled) {
        formdata.append('recording_status', 'ENABLED');
        formdata.append('status', 'ENABLED');
        formdata.append('purpose', 'quality assurance');
        formdata.append('recording_purpose', 'quality assurance');
        formdata.append('announcement_language', 'en_US');
      }

      const response = await fetch(endpoint, { method: 'POST', headers: myHeaders, body: formdata, redirect: 'follow' });
      const result = await response.json();
      if (result.status === true) {
        setIsTimerActive(true);
        if (caller_detils?.current?.id) {
          try { sessionStorage.setItem('active_accepted_call_id', caller_detils.current.id); } catch (_) {}
        }
        if (typeof setCallingStatus === 'function') {
          setCallingStatus('Connected');
        }
      } else {
        ErrorToastContainer(result.message || 'Call Accept Failed!');
        closeModal();
      }
    } catch (_) {
      ErrorToastContainer('Call Accept Error!');
      closeModal();
    } finally {
      setIsConnecting(false);
    }
  }

  const callEnd = useCallback(
    async (action) => {
      stopRingtone();
      const callId = caller_detils?.current?.id;
      closeModal();

      if (!callId) return;

      const token = getToken();
      const myHeaders = new Headers({ Authorization: `Bearer ${token}` });
      const formdata = new FormData();
      formdata.append('call_id', callId);
      formdata.append('action', action);
      formdata.append('source', 'web');

      fetch(`${domain()}/call_terminate`, { method: 'POST', headers: myHeaders, body: formdata, redirect: 'follow' })
        .then(r => r.json())
        .then(result => {
          if (result.status !== true) {
            console.warn('Call terminate API status:', result.message);
          }
        })
        .catch((err) => console.error('Call Terminate Request Error:', err));
    },
    [caller_detils, closeModal]
  );

  // Close popup and disconnect call
  const handleClosePopup = useCallback(() => {
    if (isUserInitiated && !isTimerActive) {
      callEnd('reject');
    } else {
      callEnd('terminate');
    }
  }, [isUserInitiated, isTimerActive, callEnd]);

  if (!visible || (isUserInitiated && !isIncomingCallEnabled)) return null;

  return (
    <div
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: '340px',
        zIndex: 99999,
        borderRadius: '16px',
        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.08)',
        background: '#ffffff',
        overflow: 'hidden',
        userSelect: 'none',
        transition: isDragging ? 'none' : 'box-shadow 0.2s ease',
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`Call with ${name}`}
    >
      {/* ── Drag Handle Header Bar (WhatsApp Desktop Style) ── */}
      <div
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
        style={{
          background: 'linear-gradient(90deg, #111b21 0%, #1f2c34 100%)',
          color: '#ffffff',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
      >
        <div className="d-flex align-items-center gap-2" style={{ pointerEvents: 'none' }}>
          <FontAwesomeIcon icon={faGripLines} style={{ opacity: 0.6, fontSize: '13px' }} />
          <span style={{ fontSize: '13px', fontWeight: '600', letterSpacing: '0.3px', color: '#e9edef' }}>
            WhatsApp Call
          </span>
        </div>

        {/* Close Button: Hangs up and closes window */}
        <button
          type="button"
          onClick={handleClosePopup}
          title="Close window and end call"
          aria-label="Close call window"
          style={{
            background: 'transparent',
            border: 'none',
            color: '#aebac1',
            fontSize: '15px',
            cursor: 'pointer',
            padding: '2px 6px',
            borderRadius: '4px',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#ffffff';
            e.currentTarget.style.background = '#ea4335';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#aebac1';
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* ── Card Body ── */}
      <div
        className="text-center p-4 d-flex flex-column justify-content-between align-items-center"
        style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          minHeight: '360px',
        }}
      >
        {/* Top Header / Avatar */}
        <div className="d-flex flex-column align-items-center w-100">
          <div
            className="position-relative mb-3 d-flex align-items-center justify-content-center"
            style={{
              width: '90px',
              height: '90px',
              borderRadius: '50%',
              background: '#25D366',
              boxShadow: isTimerActive
                ? '0 0 0 10px rgba(37, 211, 102, 0.18)'
                : '0 0 0 10px rgba(13, 110, 253, 0.12)',
              transition: 'all 0.3s ease',
            }}
          >
            {ProfilePic?.length === 1 || !ProfilePic ? (
              <span className="text-white fw-bold display-6">{ProfilePic || name.charAt(0)}</span>
            ) : (
              <img
                src={ProfilePic}
                alt={`${name} profile`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                className="rounded-circle"
              />
            )}
          </div>

          <h5 className="fw-bold mb-1 text-dark" style={{ fontSize: '18px' }}>
            {name}
          </h5>
          <span className="text-muted small mb-2" style={{ letterSpacing: '0.3px' }}>
            {phone}
          </span>

          {!isTimerActive && (
            <span
              className={`badge ${
                isConnecting
                  ? 'bg-info-subtle text-info-emphasis border-info-subtle'
                  : isUserInitiated
                  ? 'bg-primary-subtle text-primary border-primary-subtle'
                  : 'bg-warning-subtle text-warning-emphasis border-warning-subtle'
              } border px-3 py-1 rounded-pill d-inline-flex align-items-center gap-1`}
              style={{ fontSize: '12px', fontWeight: '500' }}
            >
              {isConnecting && (
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                  style={{ width: '10px', height: '10px' }}
                ></span>
              )}
              {isConnecting ? 'Connecting...' : calling_status}
            </span>
          )}

          {isTimerActive && <CallTimer active={isTimerActive} />}
        </div>

        {/* Mid Controls (Mute / Speaker Mute) */}
        {(isTimerActive || isBusinessInitiated) && (
          <div className="d-flex justify-content-center gap-3 my-3">
            <button
              type="button"
              className={`btn btn-circle ${isMuted ? 'btn-danger' : 'btn-light border shadow-sm'}`}
              style={{ width: '48px', height: '48px', borderRadius: '50%' }}
              onClick={handleToggleMute}
              title={isMuted ? 'Unmute Microphone' : 'Mute Microphone'}
              aria-label="Mute microphone toggle"
            >
              <FontAwesomeIcon icon={isMuted ? faMicrophoneSlash : faMicrophone} />
            </button>

            <button
              type="button"
              className={`btn btn-circle ${isSpeakerMuted ? 'btn-danger' : 'btn-light border shadow-sm'}`}
              style={{ width: '48px', height: '48px', borderRadius: '50%' }}
              onClick={handleToggleSpeaker}
              title={isSpeakerMuted ? 'Unmute Speaker' : 'Mute Speaker'}
              aria-label="Mute speaker toggle"
            >
              <FontAwesomeIcon icon={isSpeakerMuted ? faVolumeMute : faVolumeHigh} />
            </button>
          </div>
        )}

        {/* Bottom Call Action Buttons */}
        <div className="w-100 d-flex justify-content-center gap-3 mt-3">
          {/* Incoming Call: Accept / Decline */}
          {isUserInitiated && !isTimerActive && (
            <>
              <button
                type="button"
                className="btn btn-success d-flex align-items-center gap-2 px-4 py-2 shadow-sm"
                style={{ borderRadius: '30px', fontWeight: '600' }}
                onClick={callAccept}
                disabled={isConnecting}
                aria-label="Accept call"
              >
                {isConnecting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Connecting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPhone} /> Accept
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-danger d-flex align-items-center gap-2 px-4 py-2 shadow-sm"
                style={{ borderRadius: '30px', fontWeight: '600' }}
                onClick={() => callEnd('reject')}
                aria-label="Decline call"
              >
                <FontAwesomeIcon icon={faPhoneSlash} /> Decline
              </button>
            </>
          )}

          {/* Active Call or Outgoing: End Call */}
          {(isBusinessInitiated || isTimerActive) && (
            <button
              type="button"
              className="btn btn-danger d-flex align-items-center gap-2 px-4 py-2 shadow-sm"
              style={{ borderRadius: '30px', fontWeight: '600' }}
              onClick={() => callEnd('terminate')}
              aria-label="End call"
            >
              <FontAwesomeIcon icon={faPhoneSlash} /> End Call
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(Call);